import type { ActionType, AutonomyMode, PermissionAudience, PermissionInput, PermissionResult } from "./types";
import { evaluatePolicy } from "../policies/policy-engine.service";

export const ACTION_TYPES: ActionType[] = [
  "request_eta",
  "request_tracking",
  "request_pickup_confirmation",
  "request_delivery_confirmation",
  "request_pod",
  "send_customer_update",
  "share_customer_performance_report",
  "draft_post_load",
  "post_load",
  "update_posting",
  "expire_posting",
  "search_capacity",
  "rank_capacity",
  "record_carrier_response",
  "book_carrier",
  "tender_carrier",
  "approve_accessorial",
  "override_compliance",
  "create_load",
  "update_load_freight",
  "cancel_load",
  "split_load",
  "compute_margin",
  "generate_spot_quote",
  "send_customer_quote",
  "apply_accessorial",
  "recalc_fuel_surcharge",
  "generate_rate_confirmation",
  "send_tender_email",
  "send_tender_edi204",
  "capture_carrier_acceptance",
  "assign_driver_truck_trailer",
  "generate_bol",
  "request_pod_upload",
  "parse_pod_ocr",
  "attach_lumper_receipt",
  "generate_customer_invoice",
  "run_carrier_settlement_batch",
  "apply_quick_pay_discount",
  "redirect_payment_to_factor",
  "place_carrier_payment_hold",
  "release_carrier_payment_hold",
  "start_detention_clock",
  "bill_tonu",
  "open_osd_claim",
  "open_cargo_claim",
  "open_customer_dispute",
  "record_coi_expiry",
  "record_noa",
  "add_carrier_to_dnu",
  "compare_insurance_to_load_value",
  "push_accounting_transaction",
];

export const PERMISSION_AUDIENCES: PermissionAudience[] = [
  "carrier",
  "customer",
  "internal",
  "load_board",
  "factor",
];

export const AUTONOMY_PROGRESSION: AutonomyMode[] = [
  "observe",
  "draft",
  "ask_before_acting",
  "auto_execute_low_risk",
];

type PermissionCtx = {
  db: Pick<import("../../../convex/_generated/server").QueryCtx["db"], "query"> &
    Partial<Pick<import("../../../convex/_generated/server").MutationCtx["db"], "insert">>;
};

export async function evaluatePermission(
  ctx: PermissionCtx,
  input: PermissionInput,
): Promise<ReturnType<typeof evaluatePolicy>> {
  const policy = await ctx.db
    .query("permissionPolicies")
    .withIndex("by_tenant_action", (q) =>
      q.eq("tenantId", input.tenantId)
        .eq("actionType", input.actionType)
        .eq("audience", input.audience),
    )
    .unique();

  const result = evaluatePolicy(policy, input);
  await writeTelemetryIfAvailable(ctx, input, result);
  return result;
}

export function nextAutonomyMode(currentMode: AutonomyMode): AutonomyMode | null {
  if (currentMode === "emergency_lock") return null;
  const currentIndex = AUTONOMY_PROGRESSION.indexOf(currentMode);
  if (currentIndex === -1 || currentIndex === AUTONOMY_PROGRESSION.length - 1) return null;
  return AUTONOMY_PROGRESSION[currentIndex + 1] ?? null;
}

export function buildDefaultPermissionPolicies(tenantId: string, now = Date.now()) {
  return ACTION_TYPES.flatMap((actionType) =>
    PERMISSION_AUDIENCES.map((audience) => ({
      tenantId,
      actionType,
      audience,
      currentMode: "ask_before_acting" as const,
      defaultMode: "ask_before_acting" as const,
      permissionLevel: "ask_for_permission" as const,
      defaultPermissionLevel: "ask_for_permission" as const,
      nonBypassableRules: [] as string[],
      createdAt: now,
      updatedAt: now,
    })),
  );
}

async function writeTelemetryIfAvailable(
  ctx: PermissionCtx,
  input: PermissionInput,
  result: PermissionResult,
) {
  if (!ctx.db.insert) return;

  await ctx.db.insert("permissionTelemetry", {
    tenantId: input.tenantId,
    loadId: input.loadId,
    actionType: input.actionType,
    audience: input.audience,
    decision: result.decision,
    grantedMode: result.grantedAutonomyMode,
    grantedPermissionLevel: result.grantedPermissionLevel,
    agentSuggestion: input.agentSuggestion ?? {},
    createdAt: Date.now(),
  });
}
